import { MediaGrid } from "theme";

class MediaGridLightbox extends MediaGrid {
  constructor() {
    super();
    this.focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleFormInput = this.handleFormInput.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleShortlistRemoveClick = this.handleShortlistRemoveClick.bind(this);
    this.boundClose = this.close.bind(this);
    this.selectedFilters = new Map();
    this.filterSelects = new Map();
    this.itemsData = [];
    this.parsedItems = [];
    this.shortlistButtons = new Map();
    this.shortlistItemsByKey = new Map();
    this.selectedShortlistKeys = new Set();
    this.feedbackHideTimeout = null;
    this.isFreshSuccessState = false;
    this.storageAvailable = null;
    this.lightboxShortlistButton = null;
    this.lightboxShortlistSelectionKey = null;
  }

  connectedCallback() {
    this.shortlistButtons.clear();
    this.shortlistItemsByKey.clear();
    this.selectedShortlistKeys = new Set();
    this.feedbackElements = [];
    this.clearLightboxShortlistButton();
    this.loadDataAndRender();

    if (typeof super.connectedCallback === "function") {
      super.connectedCallback();
    }

    this.items = Array.from(this.querySelectorAll("[data-media-grid-item]"));
    this.modal = this.querySelector("[data-media-grid-modal]");

    if (!this.modal) {
      return;
    }

    this.modalPanel = this.modal.querySelector(".media-grid-lightbox__modal-panel");
    this.modalMedia = this.modal.querySelector("[data-media-grid-modal-media]");
    this.modalText = this.modal.querySelector("[data-media-grid-modal-text]");
    this.modalCta = this.modal.querySelector("[data-media-grid-modal-cta]");
    this.closeButton = this.modal.querySelector("[data-media-grid-close]");
    this.overlay = this.modal.querySelector("[data-media-grid-overlay]");
    this.modalDefaultLabel = this.dataset.modalLabel || this.modalPanel?.getAttribute("aria-label") || "";

    const sectionRoot = this.closest(".shopify-section");
    this.filtersContainer = sectionRoot?.querySelector("[data-media-grid-filters]") || null;

    this.parsedItems = this.items.map((item, index) => ({
      element: item,
      tags: this.itemsData[index]?.tags || [],
      data: this.itemsData[index] || null
    }));
    this.categoryOptions = this.buildCategoryOptions(this.parsedItems);
    this.shortlistItemsByKey = new Map(
      this.parsedItems
        .filter(({ data }) => data?.selection_key)
        .map(({ data }) => [data.selection_key, data])
    );

    this.items.forEach((item) => {
      item.addEventListener("click", this.handleTriggerClick);
      item.addEventListener("keydown", this.handleTriggerKeydown);
    });

    if (this.filtersContainer && this.categoryOptions.size) {
      this.renderFilters();
    }

    this.closeButton?.addEventListener("click", this.boundClose);
    this.overlay?.addEventListener("click", this.boundClose);
    document.addEventListener("keydown", this.handleDocumentKeydown);

    this.applyFilters();
    this.initializeInspirationShortlist();
  }

  get inspirationEnabled() {
    return this.dataset.enableInspirationForm === "true";
  }

  get maxShortlistItems() {
    const value = Number.parseInt(this.dataset.inspirationMaxItems || "5", 10);
    return Number.isFinite(value) && value > 0 ? value : 5;
  }

  get storageKey() {
    return `media-grid-lightbox:${this.dataset.sectionId || "default"}:inspiration`;
  }

  get formStatus() {
    return this.drawer?.dataset.inspirationFormStatus || "idle";
  }

  get limitMessage() {
    return this.dataset.inspirationLimitMessage || `You can shortlist up to ${this.maxShortlistItems} images.`;
  }

  loadDataAndRender() {
    const dataScript = this.querySelector("[data-media-grid-data]");
    const container = this.querySelector("[data-media-grid-container]");

    if (!dataScript || !container) {
      return;
    }

    try {
      this.itemsData = JSON.parse(dataScript.textContent || "[]");
    } catch (error) {
      console.warn("Unable to parse media grid data", error);
      this.itemsData = [];
      return;
    }

    const sectionWidth = this.dataset.sectionWidth || "lg";
    container.innerHTML = "";

    this.itemsData.forEach((itemData, index) => {
      const itemElement = this.createGridItem(itemData, index, sectionWidth);
      container.appendChild(itemElement);
    });
  }

  createGridItem(data, index, sectionWidth) {
    const item = document.createElement("div");
    item.className = "media-grid-lightbox__item";
    item.setAttribute("role", "button");
    item.setAttribute("tabindex", "0");
    item.setAttribute("data-media-grid-item", "");
    item.setAttribute("data-media-grid-index", index.toString());
    item.setAttribute("data-media-grid-title", data.title_plain || "Media item");
    item.style.cssText = `--media-grid-lightbox-item-column-span: ${data.column_count}; --media-grid-lightbox-item-row-span: ${data.row_count}`;

    if (data.selection_key) {
      item.dataset.mediaGridSelectionKey = data.selection_key;
    }

    if (data.type) {
      item.dataset.mediaGridType = data.type;
    }

    if (data.shopify_attributes) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = `<div ${data.shopify_attributes}></div>`;
      const attrs = tempDiv.firstElementChild?.attributes;

      if (attrs) {
        for (let i = 0; i < attrs.length; i += 1) {
          item.setAttribute(attrs[i].name, attrs[i].value);
        }
      }
    }

    const contentOverMedia = document.createElement("div");
    contentOverMedia.className = `content-over-media color-scheme color-scheme--${data.color_scheme_id} group`;
    contentOverMedia.setAttribute("reveal-on-scroll", "true");
    contentOverMedia.style.cssText = `--content-over-media-overlay: ${data.overlay_color_rgb} / ${data.overlay_opacity};`;

    contentOverMedia.appendChild(this.createPrimaryMedia(data, sectionWidth));

    const overlayText = this.createOverlayText(data);

    if (overlayText) {
      contentOverMedia.appendChild(overlayText);
    }

    item.appendChild(contentOverMedia);
    return item;
  }

  createPrimaryMedia(data, sectionWidth) {
    const fragment = document.createDocumentFragment();

    if (data.type === "image") {
      if (data.image) {
        const img = document.createElement("img");
        img.className = "zoom-image group-hover:zoom";
        img.loading = "lazy";
        img.alt = data.image.alt || "";

        const columnCount = Number.parseInt(data.column_count, 10) || 3;

        if (sectionWidth === "full") {
          img.sizes = `(max-width: 699px) 100vw, ${Math.round(100 / 12 * columnCount)}vw`;
        } else {
          img.sizes = `(max-width: 699px) 100vw, ${Math.round(1260 / 12 * columnCount)}px`;
        }

        const widths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600];
        img.srcset = widths.map((width) => `${this.getImageUrl(data.image.url, width)} ${width}w`).join(", ");
        img.src = this.getImageUrl(data.image.url, 800);

        fragment.appendChild(img);
      } else {
        fragment.appendChild(this.createPlaceholder());
      }
    } else if (data.video?.sources?.length) {
      fragment.appendChild(this.createVideoElement(data.video, data.autoplay, data.show_controls));
    } else {
      fragment.appendChild(this.createPlaceholder());
    }

    return fragment;
  }

  createVideoElement(videoData, autoplay, showControls) {
    const video = document.createElement("video");
    video.className = "zoom-image group-hover:zoom";
    video.loop = true;
    video.playsInline = true;

    if (autoplay) {
      video.autoplay = true;
      video.muted = true;
    }

    if (showControls) {
      video.controls = true;
    }

    videoData.sources.forEach((source) => {
      const sourceEl = document.createElement("source");
      sourceEl.src = source.url;
      sourceEl.type = source.mime_type;
      video.appendChild(sourceEl);
    });

    return video;
  }

  createPlaceholder() {
    const placeholder = document.createElement("div");
    placeholder.className = "placeholder zoom-image group-hover:zoom";
    placeholder.style.cssText = "aspect-ratio: 1; background: var(--color-border);";
    return placeholder;
  }

  createOverlayText(data) {
    if (!data.title && !data.content && !data.link_text) {
      return null;
    }

    const overlayDiv = document.createElement("div");
    overlayDiv.className = `prose prose--tight ${data.text_position || "place-self-center text-center"}`;

    if (data.title) {
      const heading = document.createElement("p");
      heading.className = data.heading_tag || "h4";
      heading.innerHTML = data.title;
      overlayDiv.appendChild(heading);
    }

    if (data.content) {
      const contentDiv = document.createElement("div");
      contentDiv.innerHTML = data.content;
      overlayDiv.appendChild(contentDiv);
    }

    if (data.link_text) {
      const linkUrl = data.link_url || "/pages/book-now";
      overlayDiv.appendChild(this.createButton(linkUrl, data.link_text, data.link_style, false));
    }

    return overlayDiv;
  }

  createButton(href, text, style, stretch) {
    const isLink = href && href !== "";
    const element = document.createElement(isLink ? "a" : "button");
    let className = "";

    if (style === "link") {
      className = "link";
    } else {
      className = "button";

      if (style === "outline") {
        className += " button--outline";
      }
    }

    if (stretch) {
      className += " w-full";
    }

    element.className = className;
    element.textContent = text;

    if (isLink) {
      element.href = href;
    } else {
      element.type = "button";
    }

    return element;
  }

  getImageUrl(baseUrl, width) {
    return baseUrl.replace(/(&width=)\d+/, `$1${width}`).replace(/(width=)\d+/, `$1${width}`);
  }

  getSizedImageUrl(baseUrl, width) {
    return baseUrl ? this.getImageUrl(baseUrl, width) : "";
  }

  disconnectedCallback() {
    this.close();
    this.items?.forEach((item) => {
      item.removeEventListener("click", this.handleTriggerClick);
      item.removeEventListener("keydown", this.handleTriggerKeydown);
    });
    this.filterSelects?.forEach((select) => select.removeEventListener("change", this.handleSelectChange));
    this.closeButton?.removeEventListener("click", this.boundClose);
    this.overlay?.removeEventListener("click", this.boundClose);
    this.form?.removeEventListener("input", this.handleFormInput);
    this.form?.removeEventListener("change", this.handleFormInput);
    this.form?.removeEventListener("submit", this.handleFormSubmit);
    this.shortlistList?.removeEventListener("click", this.handleShortlistRemoveClick);
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    window.clearTimeout(this.feedbackHideTimeout);

    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
  }

  handleTriggerClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const interactiveTarget = target?.closest("a, button, input, select, textarea");

    if (interactiveTarget && interactiveTarget !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    const trigger = event.currentTarget?.closest("[data-media-grid-item]");

    if (trigger) {
      this.open(trigger);
    }
  }

  handleTriggerKeydown(event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.open(event.currentTarget);
    }
  }

  handleSelectChange(event) {
    const category = event.currentTarget?.dataset.mediaGridFilterCategory || "";
    const selectedValue = event.currentTarget?.value || "";
    this.selectedFilters.set(category, selectedValue || null);
    this.applyFilters();
    this.updateClearButtonVisibility();
  }

  applyFilters() {
    if (!this.items?.length) {
      return;
    }

    this.items.forEach((item, index) => {
      const tags = this.parsedItems?.[index]?.tags || [];
      item.hidden = !this.matchesSelections(tags);
    });

    this.updateOptionAvailability();
  }

  matchesSelections(tags) {
    if (!this.selectedFilters?.size) {
      return true;
    }

    for (const [categoryHandle, selectedTag] of this.selectedFilters.entries()) {
      if (!selectedTag) {
        continue;
      }

      const matches = tags?.some((tag) => tag.category_handle === categoryHandle && tag.tag_handle === selectedTag);

      if (!matches) {
        return false;
      }
    }

    return true;
  }

  buildCategoryOptions(items) {
    const categories = new Map();

    items?.forEach(({ tags }) => {
      tags?.forEach((tag) => {
        const categoryHandle = tag.category_handle;
        const tagHandle = tag.tag_handle;

        if (!categoryHandle || !tagHandle) {
          return;
        }

        if (!categories.has(categoryHandle)) {
          categories.set(categoryHandle, {
            label: tag.category || tag.category_handle || categoryHandle,
            options: new Map()
          });
        }

        const category = categories.get(categoryHandle);

        if (!category.options.has(tagHandle)) {
          category.options.set(tagHandle, {
            label: tag.tag || tag.tag_handle || tagHandle
          });
        }
      });
    });

    return categories;
  }

  renderFilters() {
    if (!this.filtersContainer || !this.categoryOptions?.size) {
      return;
    }

    if (this.clearWrapper) {
      this.clearWrapper.remove();
      this.clearWrapper = null;
    }

    this.filtersContainer.innerHTML = "";
    this.filterSelects.clear();
    this.selectedFilters.clear();

    const sectionId = this.dataset.sectionId || "default";

    this.categoryOptions.forEach((category, categoryHandle) => {
      const wrapper = document.createElement("div");
      wrapper.className = "media-grid-lightbox__filter-group";

      const label = document.createElement("label");
      label.className = "media-grid-lightbox__filter-label";

      const selectId = `media-grid-filter-${categoryHandle}-${sectionId}`;
      label.setAttribute("for", selectId);
      label.textContent = category.label || categoryHandle;

      const select = document.createElement("select");
      select.id = selectId;
      select.className = "media-grid-lightbox__filter-select";
      select.dataset.mediaGridFilterCategory = categoryHandle;
      select.append(new Option("Any", "", true, true));

      const sortedOptions = Array.from(category.options.entries()).sort((optionA, optionB) => {
        const labelA = (optionA[1].label || optionA[0]).toLowerCase();
        const labelB = (optionB[1].label || optionB[0]).toLowerCase();
        return labelA.localeCompare(labelB);
      });

      sortedOptions.forEach(([optionHandle, option]) => {
        select.append(new Option(option.label || optionHandle, optionHandle, false, false));
      });

      select.addEventListener("change", this.handleSelectChange);
      wrapper.append(label, select);
      this.filtersContainer.append(wrapper);
      this.filterSelects.set(categoryHandle, select);
      this.selectedFilters.set(categoryHandle, null);
    });

    const clearWrapper = document.createElement("div");
    clearWrapper.className = "media-grid-lightbox__filter-group media-grid-lightbox__filter-group--clear";

    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = "media-grid-lightbox__filter-clear";
    clearButton.textContent = "Clear selection";
    clearButton.addEventListener("click", () => this.clearFilters());

    this.clearButton = clearButton;
    clearWrapper.append(clearButton);

    const filtersParent = this.filtersContainer.parentElement;

    if (filtersParent) {
      filtersParent.insertBefore(clearWrapper, this.filtersContainer.nextElementSibling);
    } else {
      this.filtersContainer.append(clearWrapper);
    }

    this.clearWrapper = clearWrapper;
    this.updateOptionAvailability();
  }

  clearFilters() {
    this.filterSelects.forEach((select, categoryHandle) => {
      select.value = "";
      this.selectedFilters.set(categoryHandle, null);
    });
    this.updateOptionAvailability();
    this.applyFilters();
    this.updateClearButtonVisibility();
  }

  updateClearButtonVisibility() {
    if (!this.clearButton) {
      return;
    }

    const hasActiveFilter = Array.from(this.selectedFilters.values()).some((value) => value !== null);
    this.clearButton.classList.toggle("is-visible", hasActiveFilter);
  }

  updateOptionAvailability() {
    if (!this.filterSelects?.size) {
      return;
    }

    const categoryHandles = Array.from(this.categoryOptions.keys());
    const itemsData = this.parsedItems || [];
    let selectionReset = false;

    this.filterSelects.forEach((select, categoryHandle) => {
      Array.from(select.options).forEach((option) => {
        const optionValue = option.value || null;
        const isAvailable = itemsData.some(({ tags }) => {
          if (!tags) {
            return false;
          }

          return categoryHandles.every((handle) => {
            const selectionForHandle = handle === categoryHandle ? optionValue : this.selectedFilters.get(handle);

            if (!selectionForHandle) {
              return true;
            }

            return tags.some((tag) => tag.category_handle === handle && tag.tag_handle === selectionForHandle);
          });
        });

        option.disabled = !isAvailable;
      });

      const selectedOption = Array.from(select.options).find((option) => option.selected);

      if (selectedOption?.disabled) {
        select.value = "";
        this.selectedFilters.set(categoryHandle, null);
        selectionReset = true;
      }
    });

    if (selectionReset) {
      this.applyFilters();
    }
  }

  handleize(value) {
    return value?.toString().toLowerCase().trim().replace(/['"]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "";
  }

  initializeInspirationShortlist() {
    if (!this.inspirationEnabled) {
      return;
    }

    this.shortlistShell = this.querySelector("[data-inspiration-shell]");
    this.shortlistCta = this.querySelector("[data-inspiration-cta]");
    this.countBadges = Array.from(this.querySelectorAll("[data-inspiration-count-badge]"));
    this.drawer = this.getInspirationDrawerElement();
    this.form = this.drawer?.querySelector("form") || null;
    this.formStatusMarker = this.form?.querySelector("[data-inspiration-form-status-marker]") || null;
    this.shortlistList = this.drawer?.querySelector("[data-inspiration-shortlist-list]") || null;
    this.shortlistEmptyState = this.drawer?.querySelector("[data-inspiration-shortlist-empty]") || null;
    this.shortlistCountSummary = this.drawer?.querySelector("[data-inspiration-count-summary]") || null;
    this.feedbackElements = [
      ...(this.shortlistShell ? Array.from(this.shortlistShell.querySelectorAll("[data-inspiration-feedback]")) : []),
      ...(this.drawer ? Array.from(this.drawer.querySelectorAll("[data-inspiration-feedback]")) : [])
    ];
    this.hiddenItemsInput = this.form?.querySelector('[data-inspiration-input="items"]') || null;
    this.hiddenCountInput = this.form?.querySelector('[data-inspiration-input="count"]') || null;
    this.hiddenSourcePageInput = this.form?.querySelector('[data-inspiration-input="source-page"]') || null;
    this.submitButton = this.form?.querySelector('button[type="submit"]') || null;
    this.nameInput = this.form?.querySelector('[name="contact[name]"]') || null;
    this.emailInput = this.form?.querySelector('[name="contact[email]"]') || null;
    this.phoneInput = this.form?.querySelector('[name="contact[phone]"]') || null;
    this.bodyInput = this.form?.querySelector('[name="contact[body]"]') || null;
    this.serverFormStatus = this.formStatusMarker?.dataset.inspirationFormStatusMarker || this.formStatus;
    this.isFreshSuccessState = this.serverFormStatus === "success";

    this.parsedItems.forEach(({ element, data }) => this.addShortlistButton(element, data));

    if (this.drawer && this.serverFormStatus !== "idle" && !this.drawer.open) {
      this.drawer.show(false);
    }

    if (this.isFreshSuccessState) {
      this.clearStoredState();
      this.selectedShortlistKeys = new Set();
    } else {
      this.restoreStoredState();
    }

    this.form?.addEventListener("input", this.handleFormInput);
    this.form?.addEventListener("change", this.handleFormInput);
    this.form?.addEventListener("submit", this.handleFormSubmit);
    this.shortlistList?.addEventListener("click", this.handleShortlistRemoveClick);

    this.updateSourcePageField();
    this.syncShortlistUI();
  }

  getInspirationDrawerElement() {
    const drawerId = this.dataset.inspirationDrawerId;
    return drawerId ? document.getElementById(drawerId) : null;
  }

  createShortlistButton(selectionKey) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "media-grid-lightbox__shortlist-toggle";
    button.dataset.inspirationToggle = selectionKey;
    button.setAttribute("aria-pressed", "false");
    button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 21.35 10.55 20.03C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54Z"></path>
      </svg>
    `;
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleShortlistItem(selectionKey);
    });

    return button;
  }

  clearLightboxShortlistButton() {
    if (this.lightboxShortlistButton?.isConnected) {
      this.lightboxShortlistButton.remove();
    }

    this.lightboxShortlistButton = null;
    this.lightboxShortlistSelectionKey = null;
  }

  attachLightboxShortlistButton(mainMediaContainer, itemData) {
    this.clearLightboxShortlistButton();

    if (!mainMediaContainer || !this.canShortlistItem(itemData)) {
      return;
    }

    const button = this.createShortlistButton(itemData.selection_key);
    button.classList.add("media-grid-lightbox__shortlist-toggle--lightbox");
    mainMediaContainer.appendChild(button);

    this.lightboxShortlistButton = button;
    this.lightboxShortlistSelectionKey = itemData.selection_key;
    this.updateShortlistButtonState(itemData.selection_key);
  }

  addShortlistButton(itemElement, itemData) {
    if (!itemElement || !this.canShortlistItem(itemData)) {
      return;
    }

    const button = this.createShortlistButton(itemData.selection_key);
    itemElement.appendChild(button);
    this.shortlistButtons.set(itemData.selection_key, button);
    this.updateShortlistButtonState(itemData.selection_key);
  }

  canShortlistItem(itemData) {
    return Boolean(itemData?.selection_key && itemData?.type === "image");
  }

  toggleShortlistItem(selectionKey) {
    if (!selectionKey || !this.shortlistItemsByKey.has(selectionKey)) {
      return;
    }

    this.markDraftAsDirty();

    if (this.selectedShortlistKeys.has(selectionKey)) {
      this.selectedShortlistKeys.delete(selectionKey);
      this.clearFeedback();
      this.syncShortlistUI();
      return;
    }

    if (this.selectedShortlistKeys.size >= this.maxShortlistItems) {
      this.showFeedback(this.limitMessage);
      return;
    }

    this.selectedShortlistKeys.add(selectionKey);
    this.clearFeedback();
    this.syncShortlistUI();
  }

  markDraftAsDirty() {
    if (this.isFreshSuccessState) {
      this.isFreshSuccessState = false;
    }
  }

  setShortlistButtonState(button, itemData, isSelected) {
    if (!button || !itemData) {
      return;
    }

    const selectionTitle = itemData.selection_title || itemData.title_plain || "this image";
    button.classList.toggle("is-active", isSelected);
    button.setAttribute("aria-pressed", String(isSelected));
    button.setAttribute("aria-label", isSelected ? `Remove ${selectionTitle} from shortlist` : `Save ${selectionTitle} to shortlist`);
  }

  updateShortlistButtonState(selectionKey) {
    const itemData = this.shortlistItemsByKey.get(selectionKey);

    if (!itemData) {
      return;
    }

    const isSelected = this.selectedShortlistKeys.has(selectionKey);
    this.setShortlistButtonState(this.shortlistButtons.get(selectionKey), itemData, isSelected);

    if (this.lightboxShortlistSelectionKey === selectionKey) {
      this.setShortlistButtonState(this.lightboxShortlistButton, itemData, isSelected);
    }
  }

  syncShortlistUI() {
    if (!this.inspirationEnabled) {
      return;
    }

    const selectedItems = this.getSelectedShortlistItems();
    const selectedCount = selectedItems.length;

    this.shortlistButtons.forEach((_, selectionKey) => this.updateShortlistButtonState(selectionKey));
    this.countBadges?.forEach((badge) => {
      badge.textContent = String(selectedCount);
    });

    if (this.shortlistShell) {
      this.shortlistShell.hidden = selectedCount === 0;
    }

    if (this.shortlistCountSummary) {
      this.shortlistCountSummary.textContent = `${selectedCount} of ${this.maxShortlistItems} selected`;
    }

    if (this.shortlistCta) {
      this.shortlistCta.classList.toggle("has-items", selectedCount > 0);
    }

    this.renderShortlistRows(selectedItems);
    this.updateShortlistInputs(selectedItems);
    this.updateSubmitButtonState(selectedCount);
    this.persistState();
  }

  getSelectedShortlistItems() {
    return Array.from(this.selectedShortlistKeys)
      .map((selectionKey) => this.shortlistItemsByKey.get(selectionKey))
      .filter(Boolean);
  }

  renderShortlistRows(selectedItems) {
    if (!this.shortlistList) {
      return;
    }

    this.shortlistList.innerHTML = "";

    if (!selectedItems.length) {
      this.shortlistList.hidden = true;

      if (this.shortlistEmptyState) {
        this.shortlistEmptyState.hidden = false;
      }

      return;
    }

    const fragment = document.createDocumentFragment();

    selectedItems.forEach((item) => {
      const row = document.createElement("article");
      row.className = "media-grid-lightbox__shortlist-row";

      const preview = document.createElement("div");
      preview.className = "media-grid-lightbox__shortlist-preview";

      if (item.selection_preview) {
        const image = document.createElement("img");
        image.loading = "lazy";
        image.alt = "";
        image.src = this.getSizedImageUrl(item.selection_preview, 160);
        preview.appendChild(image);
      } else {
        preview.classList.add("media-grid-lightbox__shortlist-preview--placeholder");
      }

      const body = document.createElement("div");
      body.className = "media-grid-lightbox__shortlist-body";

      const title = document.createElement("p");
      title.className = "media-grid-lightbox__shortlist-title";
      title.textContent = item.selection_title || item.title_plain || "Media item";

      body.append(title);

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "link text-sm media-grid-lightbox__shortlist-remove";
      removeButton.dataset.inspirationRemove = item.selection_key;
      removeButton.textContent = "Remove";
      removeButton.setAttribute("aria-label", `Remove ${item.selection_title || item.title_plain || "selected image"} from shortlist`);

      row.append(preview, body, removeButton);
      fragment.appendChild(row);
    });

    this.shortlistList.appendChild(fragment);
    this.shortlistList.hidden = false;

    if (this.shortlistEmptyState) {
      this.shortlistEmptyState.hidden = true;
    }
  }

  updateShortlistInputs(selectedItems) {
    const lines = selectedItems.map((item) => this.buildShortlistLine(item));

    if (this.hiddenItemsInput) {
      this.hiddenItemsInput.value = lines.join("\n");
    }

    if (this.hiddenCountInput) {
      this.hiddenCountInput.value = String(selectedItems.length);
    }

    this.updateSourcePageField();
  }

  buildShortlistLine(item) {
    const title = item.selection_title || item.title_plain || "Media item";
    const filename = item.selection_filename || title || item.selection_key || item.id || "media-item";
    return `${title} | ${filename}`;
  }

  updateSourcePageField() {
    if (!this.hiddenSourcePageInput) {
      return;
    }

    this.hiddenSourcePageInput.value = `${window.location.origin}${window.location.pathname}`;
  }

  updateSubmitButtonState(selectedCount) {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = selectedCount === 0;
  }

  handleFormInput() {
    this.markDraftAsDirty();
    this.persistState();
  }

  handleFormSubmit(event) {
    const selectedItems = this.getSelectedShortlistItems();
    this.updateShortlistInputs(selectedItems);

    if (!selectedItems.length) {
      event.preventDefault();
      this.showFeedback("Shortlist at least one image before sending.");
      return;
    }

    this.markDraftAsDirty();
    this.persistState();
  }

  handleShortlistRemoveClick(event) {
    const removeButton = event.target instanceof Element ? event.target.closest("[data-inspiration-remove]") : null;

    if (!removeButton) {
      return;
    }

    event.preventDefault();
    this.toggleShortlistItem(removeButton.dataset.inspirationRemove || "");
  }

  showFeedback(message) {
    if (!this.feedbackElements?.length) {
      return;
    }

    window.clearTimeout(this.feedbackHideTimeout);

    this.feedbackElements.forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });

    this.feedbackHideTimeout = window.setTimeout(() => {
      this.clearFeedback();
    }, 4000);
  }

  clearFeedback() {
    if (!this.feedbackElements?.length) {
      return;
    }

    window.clearTimeout(this.feedbackHideTimeout);
    this.feedbackElements.forEach((element) => {
      element.textContent = "";
      element.hidden = true;
    });
  }

  canUseSessionStorage() {
    if (this.storageAvailable !== null) {
      return this.storageAvailable;
    }

    try {
      const probeKey = `${this.storageKey}:probe`;
      window.sessionStorage.setItem(probeKey, "1");
      window.sessionStorage.removeItem(probeKey);
      this.storageAvailable = true;
    } catch (error) {
      this.storageAvailable = false;
    }

    return this.storageAvailable;
  }

  readStoredState() {
    if (!this.canUseSessionStorage()) {
      return null;
    }

    try {
      const storedState = window.sessionStorage.getItem(this.storageKey);
      return storedState ? JSON.parse(storedState) : null;
    } catch (error) {
      console.warn("Unable to restore inspiration form state", error);
      this.clearStoredState();
      return null;
    }
  }

  restoreStoredState() {
    const storedState = this.readStoredState();

    if (!storedState) {
      return;
    }

    const selectedKeys = Array.isArray(storedState.selectedKeys) ? storedState.selectedKeys : [];
    this.selectedShortlistKeys = new Set(
      selectedKeys.filter((selectionKey) => this.shortlistItemsByKey.has(selectionKey)).slice(0, this.maxShortlistItems)
    );
    this.restoreDraftValues(storedState.draft || {});
  }

  restoreDraftValues(draftValues) {
    if (this.isFreshSuccessState) {
      return;
    }

    const fieldMap = {
      name: this.nameInput,
      email: this.emailInput,
      phone: this.phoneInput,
      body: this.bodyInput
    };

    Object.entries(fieldMap).forEach(([fieldName, field]) => {
      if (!field) {
        return;
      }

      const draftValue = draftValues[fieldName];

      if (typeof draftValue === "string") {
        field.value = draftValue;
      }
    });
  }

  getDraftValues() {
    return {
      name: this.nameInput?.value || "",
      email: this.emailInput?.value || "",
      phone: this.phoneInput?.value || "",
      body: this.bodyInput?.value || ""
    };
  }

  persistState() {
    if (!this.inspirationEnabled || !this.canUseSessionStorage()) {
      return;
    }

    const selectedKeys = Array.from(this.selectedShortlistKeys).filter((selectionKey) => this.shortlistItemsByKey.has(selectionKey));
    const draftValues = this.getDraftValues();
    const hasDraftValues = Object.values(draftValues).some((value) => value.trim() !== "");

    if (this.isFreshSuccessState && selectedKeys.length === 0) {
      this.clearStoredState();
      return;
    }

    if (!selectedKeys.length && !hasDraftValues) {
      this.clearStoredState();
      return;
    }

    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify({ selectedKeys, draft: draftValues }));
    } catch (error) {
      console.warn("Unable to store inspiration form state", error);
    }
  }

  clearStoredState() {
    if (!this.canUseSessionStorage()) {
      return;
    }

    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn("Unable to clear inspiration form state", error);
    }
  }

  open(item) {
    if (!this.modal || !this.modalPanel || !this.modalMedia || !this.modalText || !this.modalCta) {
      return;
    }

    this.activeTrigger = item;
    this.previousFocus = document.activeElement;
    this.modalMedia.innerHTML = "";
    this.modalText.innerHTML = "";
    this.modalCta.innerHTML = "";
    this.clearLightboxShortlistButton();

    const itemIndex = Number.parseInt(item.dataset.mediaGridIndex || "0", 10);
    const itemData = this.itemsData[itemIndex];

    if (itemData) {
      const mediaItems = this.buildMediaArray(itemData);
      this.currentMediaItems = mediaItems;
      this.currentMediaIndex = 0;

      const mainMediaContainer = document.createElement("div");
      mainMediaContainer.className = "media-grid-lightbox__main-media";
      mainMediaContainer.setAttribute("data-lightbox-main-media", "");
      const mainMediaStage = document.createElement("div");
      mainMediaStage.className = "media-grid-lightbox__main-media-stage";
      mainMediaStage.setAttribute("data-lightbox-main-stage", "");
      mainMediaContainer.appendChild(mainMediaStage);
      this.modalMedia.appendChild(mainMediaContainer);

      if (mediaItems.length > 0) {
        mainMediaStage.appendChild(this.createLightboxMediaElement(mediaItems[0]));
      }

      this.attachLightboxShortlistButton(mainMediaContainer, itemData);

      if (mediaItems.length > 1) {
        const thumbnailsContainer = document.createElement("div");
        thumbnailsContainer.className = "media-grid-lightbox__thumbnails";

        mediaItems.forEach((media, index) => {
          const thumb = this.createThumbnail(media, index);

          if (index === 0) {
            thumb.classList.add("is-active");
          }

          thumbnailsContainer.appendChild(thumb);
        });

        this.modalMedia.appendChild(thumbnailsContainer);
      }

      const textContent = this.createLightboxText(itemData);

      if (textContent) {
        this.modalText.appendChild(textContent);
      }

      const ctaContent = this.createLightboxCta(itemData);

      if (ctaContent) {
        this.modalCta.appendChild(ctaContent);
      }
    }

    this.modalText.scrollTop = 0;
    this.modalPanel.setAttribute("aria-label", item.dataset.mediaGridTitle || this.modalDefaultLabel);
    this.modal.removeAttribute("hidden");
    this.modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("media-grid-lightbox-modal-open");
    this.closeButton?.focus({ preventScroll: true });
  }

  buildMediaArray(data) {
    const mediaItems = [];

    if (data.type === "image") {
      if (data.lightbox_video?.sources?.length) {
        const previewImage = data.lightbox_video.preview_image || data.lightbox_image || data.image;
        mediaItems.push({
          type: "video",
          data: data.lightbox_video,
          label: "Video",
          preview: previewImage
        });
      }

      if (data.lightbox_image) {
        mediaItems.push({
          type: "image",
          data: data.lightbox_image,
          label: "Lightbox image"
        });
      }

      if (data.image) {
        const isDifferent = !data.lightbox_image || data.image.url !== data.lightbox_image.url;

        if (isDifferent) {
          mediaItems.push({
            type: "image",
            data: data.image,
            label: "Image"
          });
        }
      }
    } else if (data.video?.sources?.length) {
      const preview = data.video_poster || data.video.preview_image || null;
      mediaItems.push({
        type: "video",
        data: data.video,
        autoplay: data.autoplay,
        label: "Video",
        preview
      });
    }

    if (!mediaItems.length) {
      mediaItems.push({ type: "placeholder" });
    }

    return mediaItems;
  }

  createLightboxMediaElement(media) {
    if (media.type === "video") {
      const video = this.createVideoElement(media.data, true, false);
      video.className = "";

      if (media.preview?.url) {
        video.setAttribute("poster", this.getImageUrl(media.preview.url, 1200));

        if (media.preview.width && media.preview.height) {
          video.style.aspectRatio = `${media.preview.width} / ${media.preview.height}`;
        }
      }

      const source = media.data.sources[0];

      if (source?.width && source?.height) {
        video.style.aspectRatio = `${source.width} / ${source.height}`;
      }

      return video;
    }

    if (media.type === "image") {
      const img = document.createElement("img");
      img.loading = "lazy";
      img.alt = media.data.alt || "";
      img.sizes = "(max-width: 699px) 100vw, 60vw";

      if (media.data.width && media.data.height) {
        img.style.aspectRatio = `${media.data.width} / ${media.data.height}`;
      }

      const widths = [400, 600, 800, 1000, 1200, 1600, 2000, 2400, 3000];
      img.srcset = widths.map((width) => `${this.getImageUrl(media.data.url, width)} ${width}w`).join(", ");
      img.src = this.getImageUrl(media.data.url, 1200);
      return img;
    }

    return this.createPlaceholder();
  }

  createThumbnail(media, index) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "media-grid-lightbox__thumbnail";
    button.setAttribute("data-thumbnail-index", index.toString());
    button.setAttribute("aria-label", `View ${media.label || "media"} ${index + 1}`);

    if (media.type === "video") {
      const thumbDiv = document.createElement("div");
      thumbDiv.className = "media-grid-lightbox__thumbnail-inner media-grid-lightbox__thumbnail-inner--video";

      if (media.preview?.url) {
        const img = document.createElement("img");
        img.loading = "lazy";
        img.alt = "";
        img.src = this.getImageUrl(media.preview.url, 150);
        thumbDiv.appendChild(img);
      } else {
        const source = media.data.sources[0];

        if (source?.width && source?.height) {
          thumbDiv.style.aspectRatio = `${source.width} / ${source.height}`;
        }
      }

      const playIcon = document.createElement("span");
      playIcon.className = "media-grid-lightbox__thumbnail-play";
      playIcon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
      thumbDiv.appendChild(playIcon);
      button.appendChild(thumbDiv);
    } else if (media.type === "image") {
      const img = document.createElement("img");
      img.loading = "lazy";
      img.alt = "";
      img.src = this.getImageUrl(media.data.url, 150);

      if (media.data.width && media.data.height) {
        img.style.aspectRatio = `${media.data.width} / ${media.data.height}`;
      }

      button.appendChild(img);
    }

    button.addEventListener("click", () => this.switchToMedia(index));
    return button;
  }

  switchToMedia(index) {
    if (!this.currentMediaItems || index === this.currentMediaIndex) {
      return;
    }

    const mainMediaContainer = this.modalMedia.querySelector("[data-lightbox-main-stage]");

    if (!mainMediaContainer) {
      return;
    }

    const currentVideo = mainMediaContainer.querySelector("video");

    if (currentVideo) {
      currentVideo.pause();
    }

    mainMediaContainer.innerHTML = "";
    mainMediaContainer.appendChild(this.createLightboxMediaElement(this.currentMediaItems[index]));

    const thumbnails = this.modalMedia.querySelectorAll(".media-grid-lightbox__thumbnail");
    thumbnails.forEach((thumb, thumbnailIndex) => {
      thumb.classList.toggle("is-active", thumbnailIndex === index);
    });

    this.currentMediaIndex = index;
  }

  createLightboxText(data) {
    if (!data.title && !data.content) {
      return null;
    }

    const textDiv = document.createElement("div");
    textDiv.className = "media-grid-lightbox__modal-text-content";

    if (data.title) {
      const heading = document.createElement("p");
      heading.className = data.heading_tag || "h4";
      heading.innerHTML = data.title;
      textDiv.appendChild(heading);
    }

    if (data.content) {
      const contentDiv = document.createElement("div");
      contentDiv.innerHTML = data.content;
      textDiv.appendChild(contentDiv);
    }

    return textDiv;
  }

  createLightboxCta(data) {
    if (!data.link_text) {
      return null;
    }

    const linkUrl = data.link_url || "/pages/book-now";
    return this.createButton(linkUrl, data.link_text, "solid", false);
  }

  close() {
    if (!this.modal || this.modal.hasAttribute("hidden")) {
      return;
    }

    this.modal.setAttribute("aria-hidden", "true");
    this.modal.setAttribute("hidden", "");
    this.modalPanel?.setAttribute("aria-label", this.modalDefaultLabel);
    this.modalMedia.innerHTML = "";
    this.modalText.innerHTML = "";
    this.modalCta.innerHTML = "";
    this.clearLightboxShortlistButton();
    document.body.classList.remove("media-grid-lightbox-modal-open");

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.focus({ preventScroll: true });
    } else if (this.previousFocus instanceof HTMLElement) {
      this.previousFocus.focus({ preventScroll: true });
    }

    this.activeTrigger = undefined;
    this.previousFocus = undefined;
  }

  handleDocumentKeydown(event) {
    if (!this.modal || this.modal.hasAttribute("hidden")) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const focusable = this.modal.querySelectorAll(this.focusableSelectors);

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!window.customElements.get("media-grid-lightbox")) {
  window.customElements.define("media-grid-lightbox", MediaGridLightbox);
}
